import { MRT_Row } from "material-react-table";
import type { Job } from "../../../../../api/types";
import { CREATE_NEW_SALESPERSON } from "../../../../../utilities/constants";
import { ColumnContext } from "../standard-column-types";
import { SalespersonPhoneColumnViewModel } from "./SalespersonPhone.column.types";

/**
 * Hook that provides the SalespersonPhoneColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useSalespersonPhoneColumnViewModel(context: ColumnContext): SalespersonPhoneColumnViewModel {
	/**
	 * Get combined list of props salespersons + new salespersons
	 */
	function getAllSalespersons() {
		return [...(context.props.salespersons || []), ...context.newSalespersons];
	}

	/**
	 * Check if field is editable (new salesperson)
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getIsEditable(job: Job): boolean {
		return job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON) ?? false;
	}

	/**
	 * Get current phone value for job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getPhoneValue(job: Job): string {
		const hasNewSalesperson = job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON);

		if (hasNewSalesperson) {
			return job?.newSalesperson?.phone || "";
		} else {
			// Existing salesperson - find from merged list
			const allSalespersons = getAllSalespersons();
			const selectedSalesperson = allSalespersons.find((contact) => job?.salespersonIds?.includes(contact.id));
			return selectedSalesperson?.mobilePhone || "";
		}
	}

	/**
	 * Handle phone input change (updates job and newSalespersons)
	 */
	function handleChange(jobId: string, phoneValue: string): void {
		const newJobs = [...context.jobs];
		const currentJob = newJobs.find((j) => j.id === jobId);

		if (currentJob && currentJob.newSalesperson) {
			currentJob.newSalesperson.phone = phoneValue;

			// Also update phone in newSalespersons if it exists
			const salespersonName = currentJob.newSalesperson.name;
			const updatedNewSalespersons = context.newSalespersons.map((sp) => {
				if (sp.name === salespersonName) {
					return { ...sp, mobilePhone: phoneValue };
				}
				return sp;
			});
			context.setNewSalespersons(updatedNewSalespersons);

			// Update jobs state and trigger validation
			context.setJobs(newJobs);
			context.validateJobs([currentJob], { updateState: true, clearPrevious: false });
		}
	}

	/**
	 * Handle AggregatedCell group update - updates phone for all jobs in the group
	 */
	function handleGroupChange(row: MRT_Row<Job>, phoneValue: string): void {
		// Track salesperson names that need phone updates in newSalespersons
		const salespersonNamesToUpdate = new Set<string>();

		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			if (job.newSalesperson) {
				job.newSalesperson.phone = phoneValue;
				salespersonNamesToUpdate.add(job.newSalesperson.name);
			}
		});

		// Update phone in newSalespersons for all affected salesperson names
		if (salespersonNamesToUpdate.size > 0) {
			const updatedNewSalespersons = context.newSalespersons.map((sp) => {
				if (salespersonNamesToUpdate.has(sp.name)) {
					return { ...sp, mobilePhone: phoneValue };
				}
				return sp;
			});
			context.setNewSalespersons(updatedNewSalespersons);
		}

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getPhoneValue,
		getIsEditable,
		handleChange,
		handleGroupChange,
	};
}
